// LAYOUTS DE UMA UNIDADE PAGADORA — o que a Atlas fala com esta folha.
//
// Tres coisas convivem nesta tela, e a distincao entre elas e' o ponto:
//
//   CATALOGO           o que EXISTE. Constante de codigo: incluir um layout novo
//                      e' escrever um tradutor, um teste e um documento. A tela
//                      mostra, nao edita.
//   ATRIBUICAO         o que VALE, nesta unidade, neste sentido, desta
//                      competencia em diante. Isso sim e' decisao, e por isso
//                      tem autor, justificativa e historico.
//   FORMATO DECLARADO  o que a averbadora SABE sobre como a unidade troca
//                      arquivos hoje. Existe antes de haver layout: e' o que
//                      permite dizer "pendente de inclusao" com o motivo certo,
//                      em vez de descobrir no primeiro arquivo recusado.
//
// O que a tela precisa deixar obvio, porque e' o medo de quem opera:
//
//  1. O PADRAO ATLAS E' O DEFAULT E NUNCA DEIXA DE SER ACEITO. A unidade sem
//     atribuicao nenhuma nao esta "sem configuracao" — ela esta no padrao, que
//     e' o caso certo para quase todo mundo.
//  2. NAO HA HERANCA NEM SUGESTAO. Ate a fase D da feature 009 (T059) a tela
//     mostrava, para a unidade sem atribuicao propria, o layout de quem
//     responde por ela — chamando aquilo de sugestao. O cartao onde se le "o
//     layout desta unidade" acabava mostrando o de outra organizacao. Sem
//     atribuicao propria vale o padrao Atlas, e so' ele.
//  3. RASCUNHO NAO E' ATRIBUIVEL A UNIDADE REAL, e a tela diz por que — falta
//     o ente confirmar a forma real dos arquivos. A unica excecao e' a
//     unidade de prova (`deProva`): nela o rascunho entra para a prova de
//     ponta a ponta, e o chip diz "rascunho em teste" para ninguem confundir
//     com uma atribuicao de verdade.
//  4. NIVEL 2 DA MATRIZ E' ESCOLHIVEL NO FORMATO DECLARADO, de proposito. O
//     catalogo recusa esses valores; a declaracao aceita, porque e' assim que
//     a unidade entra na fila de inclusao com o motivo escrito.

import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  DataTable,
  EstadoErro,
  EstadoVazio,
  Modal,
  Pill,
  Skeleton,
  TextareaField,
  type Column,
  type PillVariant,
} from "@atlas/ui/web";
import type {
  AdminAuditEntry,
  AdminPrefeitura,
  AtlasClient,
  LayoutAtribuicao,
  LayoutCatalogo,
  LayoutSentido,
  LayoutSituacao,
  LayoutVigente,
  LayoutsDaUnidade,
} from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { QUERY_UNIDADES_OPERAVEIS, listarUnidadesOperaveis } from "../../lib/unidades-operaveis";
import { abrirDocumentoDeLayout } from "../../lib/layout-documento";
import { LayoutChip } from "../../components/LayoutChip";
import { Field, inp } from "../prefeitura/_ui";

// Os tipos da feature 008 ainda não saem pelo índice do SDK. Derivá-los do
// cliente prende a tela ao contrato real das chamadas que ela faz — igual ao
// que o hub e a ficha fazem — sem mexer no pacote.
type LayoutsApi = AtlasClient["admin"]["layouts"];
type MatrizCenarios = Awaited<ReturnType<LayoutsApi["matriz"]>>;
type Dimensao = MatrizCenarios["dimensoes"][number]["dimensao"];
type FormatoDeclaradoApi = AtlasClient["admin"]["formatoDeclarado"];
type FormatoDeclaradoBody = NonNullable<Parameters<FormatoDeclaradoApi>[1]>;
type FormatoDeclaradoResposta = Awaited<ReturnType<FormatoDeclaradoApi>>;
type FormatoDeclarado = NonNullable<FormatoDeclaradoResposta["formatoDeclarado"]>;
type Cobertura = FormatoDeclaradoResposta["cobertura"];
type SituacaoCobertura = Cobertura["situacao"];

/**
 * Cartão com título — o padrão desta área (ver `trava-margem-monitor.tsx`).
 *
 * Existe porque a primeira versão desta tela usava `Corta` como se fosse um
 * cartão. `Corta` é o helper que TRUNCA texto numa célula de tabela:
 * `overflow: hidden`, `white-space: nowrap` e a largura recebida. Com
 * `largura={0}` ele colapsava o conteúdo inteiro — a tela existia no DOM e não
 * aparecia. Foi o teste desta tela que encontrou.
 *
 * `id` é para o hub apontar direto num cartão (`#formato-declarado`).
 */
function Secao({ titulo, id, children }: { titulo: string; id?: string; children: ReactNode }) {
  return (
    <Card id={id}>
      <h2 style={{ margin: "0 0 10px", fontSize: 16 }}>{titulo}</h2>
      {children}
    </Card>
  );
}

/**
 * A rota do documento exige token, entao a tela busca e abre o blob.
 *
 * `alert` em vez de estado de erro: e' um caminho de excecao (rota fora do ar,
 * sessao expirada), nao um fluxo — dar um lugar na tela pra ele custaria mais
 * do que ele vale.
 */
async function abrirDocumento(codigo: string): Promise<void> {
  const erro = await abrirDocumentoDeLayout(() => atlas.admin.layouts.documentoBlob(codigo, "md"));
  if (erro) alert(erro);
}

const SENTIDO_LABEL: Record<LayoutSentido, string> = {
  entrada: "Retorno (a unidade envia)",
  saida: "Movimento (a Atlas entrega)",
};

const SITUACAO_LABEL: Record<LayoutSituacao, string> = {
  disponivel: "disponível",
  rascunho: "rascunho",
  descontinuado: "descontinuado",
  teste: "teste",
};

/**
 * A situação de cobertura em palavras de quem opera. Uma tabela só, usada no
 * cabeçalho e na resposta do formato declarado — a mesma leitura nos dois
 * lugares, para o operador não ter de traduzir.
 */
const COBERTURA_LABEL: Record<SituacaoCobertura, { variant: PillVariant; rotulo: string }> = {
  padrao: { variant: "expirado", rotulo: "No padrão Atlas" },
  adaptado: { variant: "emdia", rotulo: "Adaptado" },
  compativel_disponivel: { variant: "aceita", rotulo: "Compatível, não atribuído" },
  compativel_rascunho: { variant: "pendente", rotulo: "Compatível em rascunho" },
  pendente_inclusao: { variant: "rejeitada", rotulo: "Pendente de inclusão" },
  prova: { variant: "expirado", rotulo: "Unidade de prova" },
  // Balde separado do `prova`: fora das contagens pelo mesmo motivo, mas SEM a
  // capacidade de receber layout em rascunho. O rótulo tem de dizer isso.
  teste: { variant: "expirado", rotulo: "Organização de teste" },
};

function CoberturaPill({ cobertura }: { cobertura: Cobertura }) {
  const c = COBERTURA_LABEL[cobertura.situacao] ?? { variant: "expirado" as const, rotulo: cobertura.situacao };
  return (
    <span title={cobertura.motivo ?? undefined} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      <Pill variant={c.variant}>{c.rotulo}</Pill>
      {cobertura.layoutCompativel ? (
        <code style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>{cobertura.layoutCompativel}</code>
      ) : null}
    </span>
  );
}

function mensagemDeErro(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

function competenciaCorrente(): string {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const ROTULO_CAMPO: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  color: "var(--text-dim)",
  textTransform: "uppercase",
};

/**
 * "← Hub" preserva os filtros do hub por construção (FR-005): quem veio do hub
 * chega com `state.deHub`, e voltar pelo histórico devolve a URL que o hub
 * escreveu (`?layout=&situacao=&raiz=&q=`). Quem chegou por link direto não tem
 * histórico que preste — o link leva ao hub limpo.
 */
function VoltarAoHub({ deHub }: { deHub: boolean }) {
  const navigate = useNavigate();
  const estilo: CSSProperties = {
    background: "none",
    border: "none",
    padding: 0,
    color: "var(--text-muted)",
    fontSize: 13,
    cursor: "pointer",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  };
  if (deHub) {
    return (
      <button type="button" onClick={() => navigate(-1)} style={estilo}>
        ← Hub
      </button>
    );
  }
  return (
    <Link to="/averbadora/layouts" style={estilo}>
      ← Hub
    </Link>
  );
}

export function AdminLayoutsUnidade() {
  const { id } = useParams<{ id: string }>();
  const unidadeId = Number(id);
  const idValido = Number.isInteger(unidadeId) && unidadeId > 0;
  const location = useLocation();
  const [atribuindo, setAtribuindo] = useState<LayoutSentido | null>(null);

  const prefeituras = useQuery({
    queryKey: QUERY_UNIDADES_OPERAVEIS,
    queryFn: listarUnidadesOperaveis,
  });

  const catalogo = useQuery({
    queryKey: ["admin", "layouts", "catalogo"],
    queryFn: () => atlas.admin.layouts.catalogo(),
  });

  const matriz = useQuery({
    queryKey: ["admin", "layouts", "matriz"],
    queryFn: () => atlas.admin.layouts.matriz(),
  });

  const daUnidade = useQuery({
    queryKey: ["admin", "layouts", "unidade", unidadeId],
    queryFn: () => atlas.admin.layouts.daUnidade(unidadeId),
    enabled: idValido,
  });

  const unidade = useMemo(
    () => prefeituras.data?.prefeituras.find((p) => p.id === unidadeId),
    [prefeituras.data, unidadeId],
  );
  const deProva = unidade?.deProva === true;
  const permiteRascunho = deProva || daUnidade.data?.permiteRascunho === true;

  // A situação de um layout vem do CATÁLOGO, não do vigente: o vigente sabe
  // qual código vale, o catálogo sabe se aquele código é rascunho. É o que
  // permite o chip dizer "rascunho em teste" na unidade de prova.
  const porCodigo = useMemo(
    () => new Map<string, LayoutCatalogo>((catalogo.data?.layouts ?? []).map((l) => [l.codigo, l])),
    [catalogo.data],
  );
  const doCatalogo = (codigo: string): LayoutCatalogo | undefined => porCodigo.get(codigo);

  const deHub = (location.state as { deHub?: boolean } | null)?.deHub === true;

  // O hub manda para `#formato-declarado` quando a ação é declarar. O cartão
  // existe desde o primeiro render (com `Skeleton`), então o alvo está lá.
  // jsdom não implementa `scrollIntoView`, e rolar é conveniência, não fluxo:
  // falhar aqui não pode derrubar a tela.
  useEffect(() => {
    if (location.hash !== "#formato-declarado") return;
    try {
      document.getElementById("formato-declarado")?.scrollIntoView({ block: "start" });
    } catch {
      /* sem rolagem */
    }
  }, [location.hash]);

  if (!idValido) {
    return (
      <div style={{ display: "grid", gap: 16 }}>
        <VoltarAoHub deHub={deHub} />
        <EstadoErro mensagem="Endereço sem unidade: abra a unidade a partir do hub de layouts." />
      </div>
    );
  }

  const erroDaUnidade = daUnidade.isError ? mensagemDeErro(daUnidade.error, "não consegui carregar os layouts da unidade") : null;

  return (
    // `minmax(0, 1fr)` (T081): a coluna implícita de um grid é `auto` e cresce
    // até o conteúdo mais largo — a tabela de auditoria empurraria a página
    // para a direita a 1024 px. Com o mínimo em zero, a rolagem fica no quadro
    // da tabela e a página não rola de lado (FR-038).
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0, 1fr)" }}>
      <header style={{ display: "grid", gap: 8, gridTemplateColumns: "minmax(0, 1fr)" }}>
        <div>
          <VoltarAoHub deHub={deHub} />
        </div>
        {prefeituras.isLoading ? (
          <Skeleton linhas={2} altura={22} largura="50%" />
        ) : prefeituras.isError ? (
          <EstadoErro
            mensagem={mensagemDeErro(prefeituras.error, "não consegui carregar a unidade")}
            onTentarDeNovo={() => void prefeituras.refetch()}
          />
        ) : !unidade ? (
          <EstadoErro mensagem="Unidade pagadora não encontrada — ela pode ter sido removida, ou o endereço está errado." />
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <h1 style={{ margin: 0, fontSize: 22 }}>{unidade.nome}</h1>
              {unidade.prefeituraPaiId ? <Pill variant="expirado">filial</Pill> : null}
              {unidade.deProva ? <Pill variant="pendente">unidade de prova</Pill> : null}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 13, color: "var(--text-dim)" }}>
              <span>Situação de cobertura:</span>
              {unidade.cobertura ? <CoberturaPill cobertura={unidade.cobertura} /> : <span>—</span>}
            </div>
          </>
        )}
      </header>

      <VigentesCard
        dados={daUnidade.data}
        carregando={daUnidade.isLoading}
        erro={erroDaUnidade}
        onTentarDeNovo={() => void daUnidade.refetch()}
        deProva={deProva}
        situacaoDe={(codigo) => doCatalogo(codigo)?.situacao}
        onAtribuir={(s) => setAtribuindo(s)}
      />
      <HistoricoCard
        historico={daUnidade.data?.historico ?? []}
        carregando={daUnidade.isLoading}
        erro={erroDaUnidade}
        onTentarDeNovo={() => void daUnidade.refetch()}
        doCatalogo={doCatalogo}
      />
      <AuditoriaCard unidadeId={unidadeId} />
      <FormatoDeclaradoCard
        unidadeId={unidadeId}
        unidade={unidade}
        matriz={matriz.data}
        carregando={prefeituras.isLoading || matriz.isLoading}
        erro={
          prefeituras.isError
            ? mensagemDeErro(prefeituras.error, "não consegui carregar a unidade")
            : matriz.isError
              ? mensagemDeErro(matriz.error, "não consegui carregar a matriz de cenários")
              : null
        }
        onTentarDeNovo={() => {
          if (prefeituras.isError) void prefeituras.refetch();
          if (matriz.isError) void matriz.refetch();
        }}
      />
      <CatalogoCard
        layouts={catalogo.data?.layouts ?? []}
        carregando={catalogo.isLoading}
        erro={catalogo.isError ? mensagemDeErro(catalogo.error, "não consegui carregar o catálogo") : null}
        onTentarDeNovo={() => void catalogo.refetch()}
      />

      {atribuindo ? (
        <ModalAtribuir
          unidadeId={unidadeId}
          sentido={atribuindo}
          catalogo={catalogo.data?.layouts ?? []}
          vigente={atribuindo === "entrada" ? daUnidade.data?.vigentes.entrada : daUnidade.data?.vigentes.saida}
          deProva={deProva}
          permiteRascunho={permiteRascunho}
          onClose={() => setAtribuindo(null)}
        />
      ) : null}
    </div>
  );
}

// ============================================================ vigentes

function VigentesCard({
  dados,
  carregando,
  erro,
  onTentarDeNovo,
  deProva,
  situacaoDe,
  onAtribuir,
}: {
  dados?: LayoutsDaUnidade;
  carregando: boolean;
  erro: string | null;
  onTentarDeNovo: () => void;
  deProva: boolean;
  situacaoDe: (codigo: string) => LayoutSituacao | undefined;
  onAtribuir: (s: LayoutSentido) => void;
}) {
  if (carregando) {
    return (
      <Secao titulo="Em vigor">
        <Skeleton linhas={4} />
      </Secao>
    );
  }
  if (erro) {
    return (
      <Secao titulo="Em vigor">
        <EstadoErro mensagem={erro} onTentarDeNovo={onTentarDeNovo} />
      </Secao>
    );
  }
  if (!dados) return null;

  return (
    <Secao titulo={`Em vigor na competência ${dados.competenciaReferencia}`}>
      {/*
        O AVISO DA SUGESTÃO DA RAIZ SAIU NA FASE D (T059). Ele dizia, para a
        filial sem atribuição própria, qual layout a raiz usava — com o cuidado
        de chamar aquilo de sugestão e não de herança. Mas o lugar onde se lê "o
        layout desta unidade" mostrava o de outra organização, e a leitura que
        sobrava era a errada. A migração da fase C gravou a atribuição de toda
        unidade em que isso mudava o que se via; quem não tem atribuição usa o
        padrão Atlas, e é isso que os dois cartões abaixo dizem.
      */}
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
        {(["entrada", "saida"] as const).map((sentido) => {
          const v = dados.vigentes[sentido];
          const situacao = situacaoDe(v.codigo);
          return (
            <div key={sentido} style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 4 }}>{SENTIDO_LABEL[sentido]}</div>
              <div style={{ marginBottom: 6 }}>
                <LayoutChip
                  nome={v.nome}
                  codigo={v.codigo}
                  versao={v.versao}
                  padrao={v.padrao}
                  situacao={situacao}
                  emTeste={deProva && situacao === "rascunho"}
                  comLink
                />
              </div>
              <div style={{ fontSize: 12, color: "var(--text-dim)" }}>
                {v.vigenciaCompetencia ? `desde ${v.vigenciaCompetencia}` : "nunca foi trocado"}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <Button size="sm" onClick={() => onAtribuir(sentido)}>
                  Trocar layout
                </Button>
                <Button size="sm" variant="ghost" onClick={() => void abrirDocumento(v.codigo)}>
                  documento técnico
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </Secao>
  );
}

// ============================================================ auditoria

/**
 * A TRILHA, ao lado do histórico — e elas respondem coisas diferentes.
 *
 * O histórico sai de `admin_layout_atribuicoes`: é o estado, e é ele que decide
 * qual layout vale. A auditoria sai de `admin_auditoria`: é o registro do ato,
 * com quem, quando, de onde e por quê, e sobrevive a qualquer mexida no estado.
 * Quando as duas divergem, é a segunda que vale como prova — e é justamente por
 * poder divergir que as duas aparecem separadas em vez de fundidas numa só.
 *
 * O filtro desce ao Postgres por `?acao=` e `?unidadeId=` (FR-032). Trazer
 * a trilha inteira e peneirar aqui faria "as atribuições desta unidade"
 * significar "as que couberam no limite" — resposta errada com cara de certa.
 */
function AuditoriaCard({ unidadeId }: { unidadeId: number }) {
  const q = useQuery({
    queryKey: ["admin", "auditoria", "layout_atribuido", unidadeId],
    queryFn: () =>
      atlas.admin.listAuditoria({ acao: "layout_atribuido", unidadeId, limit: 100 }),
  });
  const entradas = q.data?.entries ?? [];

  const colunas: Column<AdminAuditEntry>[] = [
    {
      key: "quando",
      header: "Quando",
      width: 150,
      render: (e) => <span style={{ fontSize: 12 }}>{new Date(e.ts).toLocaleString("pt-BR")}</span>,
    },
    {
      key: "quem",
      header: "Quem",
      width: 160,
      render: (e) => <span style={{ fontSize: 12 }}>{e.userId ?? "—"}</span>,
    },
    {
      key: "mudanca",
      header: "De → para",
      width: 200,
      render: (e) => {
        const d = (e.dados ?? {}) as { de?: string; para?: string; sentido?: string; vigenciaCompetencia?: string };
        if (!d.para) return <span style={{ opacity: 0.5 }}>—</span>;
        return (
          <div style={{ display: "grid", gap: 2, fontSize: 12 }}>
            <span>
              <code>{d.de}</code> → <code>{d.para}</code>
            </span>
            <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
              {d.sentido === "entrada" ? "retorno" : "movimento"} · desde {d.vigenciaCompetencia}
            </span>
          </div>
        );
      },
    },
    {
      key: "justificativa",
      header: "Justificativa",
      wrap: true,
      // A FRASE INTEIRA quando o payload não tiver a justificativa isolada:
      // registro gravado antes do payload estruturado só tem `detalhes`, e
      // mostrar vazio ali seria afirmar que ninguém justificou.
      render: (e) => (
        <span style={{ fontSize: 12 }}>
          {String((e.dados as { justificativa?: string } | undefined)?.justificativa ?? e.detalhes)}
        </span>
      ),
    },
  ];

  return (
    <Secao titulo="Auditoria das trocas de layout">
      <p style={{ marginTop: 0, fontSize: 13, color: "var(--text-dim)" }}>
        O registro do <b>ato</b>, não do estado: quem trocou, quando, de qual layout para qual e por quê. É o que
        responde à pergunta depois, quando ninguém lembra.
      </p>
      {q.isLoading ? (
        <Skeleton linhas={3} />
      ) : q.isError ? (
        <EstadoErro
          mensagem={mensagemDeErro(q.error, "não consegui carregar a auditoria")}
          onTentarDeNovo={() => void q.refetch()}
        />
      ) : entradas.length === 0 ? (
        <EstadoVazio
          titulo="Nenhuma troca registrada para esta unidade"
          orientacao="Ela fala o padrão Atlas nos dois sentidos."
        />
      ) : (
        <DataTable
          caption="Auditoria das trocas de layout desta unidade"
          columns={colunas}
          rows={entradas}
          rowKey={(e) => e.id}
        />
      )}
    </Secao>
  );
}

// ============================================================ historico

function HistoricoCard({
  historico,
  carregando,
  erro,
  onTentarDeNovo,
  doCatalogo,
}: {
  historico: LayoutAtribuicao[];
  carregando: boolean;
  erro: string | null;
  onTentarDeNovo: () => void;
  doCatalogo: (codigo: string) => LayoutCatalogo | undefined;
}) {
  const colunas: Column<LayoutAtribuicao>[] = [
    { key: "atribuidoEm", header: "Quando", render: (a) => new Date(a.atribuidoEm).toLocaleString("pt-BR") },
    { key: "sentido", header: "Sentido", render: (a) => (a.sentido === "entrada" ? "Retorno" : "Movimento") },
    {
      key: "mudanca",
      header: "De → para",
      wrap: true,
      // Nome e situação do "de" vêm do catálogo: a atribuição só guarda o
      // código anterior. Sem catálogo, o código é o nome — nunca fica vazio.
      render: (a) => {
        const de = doCatalogo(a.anteriorCodigo);
        const para = doCatalogo(a.layoutCodigo);
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <LayoutChip
              tamanho="sm"
              nome={de?.nome ?? a.anteriorCodigo}
              codigo={a.anteriorCodigo}
              versao={de?.versao ?? "—"}
              situacao={de?.situacao}
              padrao={de?.padrao}
            />
            <b>→</b>
            <LayoutChip
              tamanho="sm"
              nome={para?.nome ?? a.layoutCodigo}
              codigo={a.layoutCodigo}
              versao={a.layoutVersao}
              situacao={para?.situacao}
              padrao={para?.padrao}
            />
          </span>
        );
      },
    },
    { key: "vigenciaCompetencia", header: "A partir de" },
    { key: "atribuidoNome", header: "Quem" },
    { key: "justificativa", header: "Por quê", wrap: true },
  ];

  return (
    <Secao titulo="Histórico de atribuições">
      {carregando ? (
        <Skeleton linhas={3} />
      ) : erro ? (
        <EstadoErro mensagem={erro} onTentarDeNovo={onTentarDeNovo} />
      ) : historico.length === 0 ? (
        <EstadoVazio titulo="Nenhuma troca até hoje" orientacao="A unidade está no padrão Atlas desde sempre." />
      ) : (
        <DataTable caption="Histórico de atribuições de layout desta unidade" columns={colunas} rows={historico} rowKey={(a) => a.id} />
      )}
    </Secao>
  );
}

// ============================================================ formato declarado

/**
 * O que a averbadora sabe sobre como a unidade troca arquivos HOJE.
 *
 * Não é atribuição: não decide nada, não tem competência de vigência. É o
 * registro que permite a cobertura dizer "compatível com X, não atribuído" ou
 * "pendente de inclusão porque manda PDF" antes de qualquer arquivo chegar.
 */
function FormatoDeclaradoCard({
  unidadeId,
  unidade,
  matriz,
  carregando,
  erro,
  onTentarDeNovo,
}: {
  unidadeId: number;
  unidade: AdminPrefeitura | undefined;
  matriz: MatrizCenarios | undefined;
  carregando: boolean;
  erro: string | null;
  onTentarDeNovo: () => void;
}) {
  return (
    <Secao id="formato-declarado" titulo="Formato declarado">
      <p style={{ marginTop: 0, fontSize: 13, color: "var(--text-dim)" }}>
        Como esta unidade entrega e recebe arquivos hoje, no que a averbadora souber. Não muda o que está em
        vigor: serve para a cobertura dizer se algum layout do catálogo já a atende — e, se nenhum atender, com
        qual motivo ela entra na fila de inclusão.
      </p>
      {carregando ? (
        <Skeleton linhas={5} />
      ) : erro ? (
        <EstadoErro mensagem={erro} onTentarDeNovo={onTentarDeNovo} />
      ) : !unidade || !matriz ? null : (
        <FormularioFormatoDeclarado key={unidadeId} unidadeId={unidadeId} declarado={unidade.formatoDeclarado ?? null} matriz={matriz} />
      )}
    </Secao>
  );
}

function FormularioFormatoDeclarado({
  unidadeId,
  declarado,
  matriz,
}: {
  unidadeId: number;
  declarado: FormatoDeclarado | null;
  matriz: MatrizCenarios;
}) {
  const qc = useQueryClient();
  const [padrao, setPadrao] = useState(declarado?.padrao ?? false);
  const [descricao, setDescricao] = useState(declarado?.descricao ?? "");
  const [cenarios, setCenarios] = useState<Partial<Record<Dimensao, string[]>>>(declarado?.cenarios ?? {});
  const [coberturaAgora, setCoberturaAgora] = useState<Cobertura | null>(null);

  // Re-sincroniza só quando o servidor muda DE VERDADE (salvou, removeu,
  // outra sessão declarou). A comparação é pelo conteúdo: um refetch que
  // devolve o mesmo dado não pode apagar o que o operador está digitando.
  const chave = JSON.stringify(declarado);
  useEffect(() => {
    setPadrao(declarado?.padrao ?? false);
    setDescricao(declarado?.descricao ?? "");
    setCenarios(declarado?.cenarios ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  const salvar = useMutation({
    mutationFn: (body: FormatoDeclaradoBody | null) => atlas.admin.formatoDeclarado(unidadeId, body),
    onSuccess: (r) => {
      setCoberturaAgora(r.cobertura);
      // O servidor é a fonte: ao remover, o formulário volta ao vazio na hora,
      // sem esperar o refetch — e ao salvar, fica com o que foi gravado.
      setPadrao(r.formatoDeclarado?.padrao ?? false);
      setDescricao(r.formatoDeclarado?.descricao ?? "");
      setCenarios(r.formatoDeclarado?.cenarios ?? {});
      // Hub e lista de prefeituras derivam a cobertura desta declaração.
      void qc.invalidateQueries({ queryKey: ["admin", "layouts"] });
      void qc.invalidateQueries({ queryKey: ["admin", "prefeituras"] });
    },
  });

  const tamanho = descricao.trim().length;
  const descricaoOk = tamanho >= 3 && tamanho <= 500;

  const escolherValores = (dimensao: Dimensao, valores: string[]) =>
    setCenarios((atual) => {
      const proximo = { ...atual };
      if (valores.length === 0) delete proximo[dimensao];
      else proximo[dimensao] = valores;
      return proximo;
    });

  return (
    <div style={{ display: "grid", gap: 14 }}>
      {declarado ? (
        <p style={{ margin: 0, fontSize: 12, color: "var(--text-dim)" }}>
          Declarado por {declarado.declaradoPor} em {new Date(declarado.declaradoEm).toLocaleDateString("pt-BR")}
        </p>
      ) : (
        <p style={{ margin: 0, fontSize: 12, color: "var(--text-dim)" }}>
          Ainda não há declaração para esta unidade — até haver, a cobertura vale pelo que está em vigor.
        </p>
      )}

      {/* `htmlFor` explícito (T081, FR-039): o rótulo já envolve a caixa, mas a
          ligação por id é a que o teste de navegador confere e a que sobrevive
          se o campo sair de dentro do rótulo. */}
      <label htmlFor="formato-declarado-padrao" style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
        <input
          id="formato-declarado-padrao"
          type="checkbox"
          checked={padrao}
          onChange={(e) => setPadrao(e.target.checked)}
        />
        Esta unidade segue o padrão Atlas
      </label>

      <TextareaField
        label="Descrição"
        required
        hint="3 a 500 caracteres: de onde veio a informação e o que o sistema da unidade exporta e importa."
        value={descricao}
        onChange={(e) => setDescricao(e.target.value)}
        error={descricao.length > 0 && !descricaoOk ? "Entre 3 e 500 caracteres." : undefined}
      />

      <div>
        <div style={{ ...ROTULO_CAMPO, marginBottom: 6 }}>Cenários de forma</div>
        <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--text-muted)" }}>
          Marque só o que souber, por dimensão. Os valores marcados como <b>não suportado</b> podem ser escolhidos:
          é assim que a unidade entra na fila de inclusão com o motivo escrito.
          {padrao ? " Com o padrão marcado, os cenários não se aplicam." : ""}
        </p>
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
          {matriz.dimensoes.map((d) => {
            const idSelect = `formato-declarado-${d.dimensao}`;
            return (
              <div key={d.dimensao} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label htmlFor={idSelect} style={ROTULO_CAMPO}>
                  {d.rotulo}
                </label>
                <select
                  id={idSelect}
                  multiple
                  disabled={padrao}
                  value={cenarios[d.dimensao] ?? []}
                  onChange={(e) =>
                    escolherValores(
                      d.dimensao,
                      Array.from(e.target.selectedOptions, (o) => o.value),
                    )
                  }
                  style={{ ...inp, minHeight: 96 }}
                >
                  {d.valores.map((v) => (
                    <option key={v.valor} value={v.valor}>
                      {v.rotulo}
                    </option>
                  ))}
                  {d.naoSuportados.map((n) => (
                    <option key={n.valor} value={n.valor}>
                      {`${n.valor} — não suportado: ${n.motivo}`}
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{d.descricao}</span>
              </div>
            );
          })}
        </div>
      </div>

      {salvar.isError ? <EstadoErro mensagem={mensagemDeErro(salvar.error, "não consegui salvar o formato declarado")} /> : null}

      {coberturaAgora ? (
        <p role="status" style={{ margin: 0, fontSize: 13, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span>Cobertura agora:</span>
          <CoberturaPill cobertura={coberturaAgora} />
          {coberturaAgora.motivo ? <span style={{ color: "var(--text-dim)" }}>— {coberturaAgora.motivo}</span> : null}
        </p>
      ) : null}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <Button
          disabled={!descricaoOk || salvar.isPending}
          onClick={() => salvar.mutate({ padrao, descricao: descricao.trim(), cenarios: padrao ? {} : cenarios })}
        >
          {salvar.isPending ? "Salvando…" : "Salvar"}
        </Button>
        {declarado ? (
          <Button variant="ghost" disabled={salvar.isPending} onClick={() => salvar.mutate(null)}>
            Remover declaração
          </Button>
        ) : null}
      </div>
    </div>
  );
}

// ============================================================ catalogo

function CatalogoCard({
  layouts,
  carregando,
  erro,
  onTentarDeNovo,
}: {
  layouts: LayoutCatalogo[];
  carregando: boolean;
  erro: string | null;
  onTentarDeNovo: () => void;
}) {
  const colunas: Column<LayoutCatalogo>[] = [
    {
      key: "nome",
      header: "Layout",
      wrap: true,
      render: (l) => (
        <div style={{ display: "grid", gap: 4 }}>
          <LayoutChip nome={l.nome} codigo={l.codigo} versao={l.versao} situacao={l.situacao} padrao={l.padrao} comLink />
          <div style={{ fontSize: 12, color: "var(--text-dim)" }}>{l.descricao}</div>
        </div>
      ),
    },
    {
      key: "sentido",
      header: "Sentido",
      // O arquivo mensal de consignataria (#165) entra na Atlas, mas nao e'
      // retorno da folha: quem o envia e' a consignataria.
      render: (l) =>
        l.finalidade === "entrada_consignataria" ? "Consignatária" : l.sentido === "entrada" ? "Retorno" : "Movimento",
    },
    {
      key: "unidadesUsando",
      header: "Unidades",
      // Um layout tem um sentido só; a contagem que interessa é a dele.
      render: (l) => String(l.sentido === "entrada" ? l.unidadesUsando.entrada : l.unidadesUsando.saida),
    },
    {
      key: "limitacoes",
      header: "O que não carrega",
      render: (l) =>
        l.limitacoes.length === 0 ? (
          <span style={{ color: "var(--text-dim)" }}>—</span>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
            {l.limitacoes.map((x: string) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        ),
    },
    {
      key: "doc",
      header: "Documento",
      render: (l) => (
        <Button size="sm" variant="ghost" onClick={() => void abrirDocumento(l.codigo)}>
          abrir
        </Button>
      ),
    },
  ];

  return (
    <Secao titulo="Catálogo de layouts">
      <p style={{ marginTop: 0, fontSize: 13, color: "var(--text-dim)" }}>
        Incluir um layout novo é trabalho de engenharia — ele precisa de tradutor, teste e documento. Aqui a
        averbadora escolhe qual dos existentes vale em cada unidade. Layout em <b>rascunho</b> ainda depende de
        confirmações do órgão sobre a forma real dos arquivos e só pode ser atribuído a uma unidade de prova.
      </p>
      {carregando ? (
        <Skeleton linhas={4} />
      ) : erro ? (
        <EstadoErro mensagem={erro} onTentarDeNovo={onTentarDeNovo} />
      ) : layouts.length === 0 ? (
        <EstadoVazio titulo="Nenhum layout no catálogo" orientacao="O catálogo é constante de código: se está vazio, a API não está a servir o pacote esperado." />
      ) : (
        <DataTable caption="Catálogo de layouts" columns={colunas} rows={layouts} rowKey={(l) => l.codigo} />
      )}
    </Secao>
  );
}

// ============================================================ atribuir

function ModalAtribuir({
  unidadeId,
  sentido,
  catalogo,
  vigente,
  deProva,
  permiteRascunho,
  onClose,
}: {
  unidadeId: number;
  sentido: LayoutSentido;
  catalogo: LayoutCatalogo[];
  vigente?: LayoutVigente;
  deProva: boolean;
  permiteRascunho: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [codigo, setCodigo] = useState<string>(vigente?.codigo ?? "");
  const [competencia, setCompetencia] = useState(competenciaCorrente());
  const [justificativa, setJustificativa] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  // So' arquivo da FOLHA da unidade: a carga inicial, o arquivo mensal de
  // consignataria (#165) e o retorno a consignataria (#189) nao se atribuem.
  const doSentido = catalogo.filter(
    (l) => l.sentido === sentido && l.finalidade !== "carga_inicial"
      && l.finalidade !== "entrada_consignataria" && l.finalidade !== "retorno_consignataria",
  );
  const escolhido = doSentido.find((l) => l.codigo === codigo);
  // Homolog/producao restringem o rascunho a unidade de prova. Localmente a API
  // libera a atribuicao para exercitar o ciclo em cadastros descartaveis.
  const atribuivel = (l: LayoutCatalogo) =>
    l.situacao === "disponivel" || (permiteRascunho && l.situacao === "rascunho");
  const naoAtribuivel = escolhido ? !atribuivel(escolhido) : false;
  const rascunhoEmTeste = escolhido?.situacao === "rascunho" && permiteRascunho;

  const atribuir = useMutation({
    mutationFn: () =>
      atlas.admin.layouts.atribuir(unidadeId, {
        sentido,
        layoutCodigo: codigo as LayoutCatalogo["codigo"],
        vigenciaCompetencia: competencia,
        justificativa,
      }),
    onSuccess: (r) => {
      setErro(null);
      void qc.invalidateQueries({ queryKey: ["admin", "layouts"] });
      void qc.invalidateQueries({ queryKey: ["admin", "prefeituras"] });
      // Aviso NAO fecha o modal: ele diz que o movimento ja emitido daquela
      // competencia nao muda, e fechar por cima disso esconderia a informacao.
      if (r.aviso) setAviso(r.aviso);
      else onClose();
    },
    onError: (e: unknown) => setErro(mensagemDeErro(e, "não consegui atribuir")),
  });

  const pronto = codigo !== "" && !naoAtribuivel && justificativa.trim().length >= 3 && /^\d{6}$/.test(competencia);

  return (
    <Modal open onClose={onClose} title={`Trocar o layout de ${sentido === "entrada" ? "retorno" : "movimento"}`}>
      {aviso ? (
        <div style={{ border: "1px solid var(--warn)", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 13 }}>
          <b>Atribuído.</b> {aviso}
          <div style={{ marginTop: 8 }}>
            <Button size="sm" onClick={onClose}>
              Entendi
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {/* `para`/`id` em todo campo do diálogo (T081, FR-039): o `Field`
              envolve o campo, mas a ligação explícita é a que o teste de
              navegador confere — e é ela que nomeia o campo para o leitor de
              tela mesmo com o diálogo aberto por cima da página. */}
          <Field lbl="Layout" required para="atribuir-layout-codigo">
            <select id="atribuir-layout-codigo" style={inp} value={codigo} onChange={(e) => setCodigo(e.target.value)}>
              <option value="">escolha…</option>
              {/* RASCUNHO E DESCONTINUADO APARECEM, E DESABILITADOS.
                  Escondê-los faria o operador procurar um layout que ele sabe
                  existir e não achar; deixá-los clicáveis faria ele tomar um
                  422 sem entender. Visível e travado é o único jeito de a tela
                  dizer "existe, mas ainda não". A unidade de prova é a exceção
                  para o rascunho — e só para ele. */}
              {doSentido.map((l) => (
                <option key={l.codigo} value={l.codigo} disabled={!atribuivel(l)}>
                  {l.nome} (v{l.versao})
                  {l.situacao !== "disponivel" ? ` — ${SITUACAO_LABEL[l.situacao] ?? l.situacao}` : ""}
                </option>
              ))}
            </select>
          </Field>

          {escolhido ? (
            <LayoutChip
              nome={escolhido.nome}
              codigo={escolhido.codigo}
              versao={escolhido.versao}
              situacao={escolhido.situacao}
              padrao={escolhido.padrao}
              emTeste={rascunhoEmTeste}
            />
          ) : null}

          {naoAtribuivel ? (
            <div style={{ fontSize: 13, color: "var(--danger)" }}>
              {escolhido!.situacao === "rascunho"
                ? (escolhido!.pendencias.length > 0
                    ? "Este layout está em rascunho: faltam confirmações do órgão sobre a forma real dos arquivos. "
                    : "Este layout está em rascunho: as confirmações do órgão já vieram, falta a prova de ponta a ponta em homologação. ") +
                  "Enquanto isso, a unidade continua no padrão Atlas."
                : "Este layout foi descontinuado e não pode ser atribuído a uma unidade nova."}
            </div>
          ) : rascunhoEmTeste ? (
            <div style={{ fontSize: 13, background: "var(--surface-2)", borderRadius: 8, padding: "8px 10px" }}>
              {deProva
                ? "Esta unidade é de prova: o rascunho pode ser atribuído a ela para a prova de ponta a ponta."
                : "Ambiente local: o rascunho pode ser atribuído para testar o ciclo completo antes da homologação."}
            </div>
          ) : null}

          {escolhido && escolhido.limitacoes.length > 0 ? (
            <div style={{ fontSize: 12, background: "var(--surface-2)", borderRadius: 8, padding: "8px 10px" }}>
              <b>O que este layout não carrega:</b>
              <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
                {escolhido.limitacoes.map((x: string) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <Field
            lbl="Vale a partir da competência"
            required
            hint="AAAAMM. A competência anterior continua no layout de hoje."
            para="atribuir-layout-competencia"
          >
            <input
              id="atribuir-layout-competencia"
              inputMode="numeric"
              style={inp}
              value={competencia}
              onChange={(e) => setCompetencia(e.target.value.replace(/\D/g, "").slice(0, 6))}
            />
          </Field>

          <Field
            lbl="Justificativa"
            required
            hint="3 a 500 caracteres. Fica no histórico e na auditoria."
            para="atribuir-layout-justificativa"
          >
            <textarea
              id="atribuir-layout-justificativa"
              style={{ ...inp, minHeight: 72 }}
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
            />
          </Field>

          {erro ? <div style={{ color: "var(--danger)", fontSize: 13 }}>{erro}</div> : null}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button disabled={!pronto || atribuir.isPending} onClick={() => atribuir.mutate()}>
              {atribuir.isPending ? "Atribuindo…" : "Atribuir"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
